/* eslint-disable react-hooks/exhaustive-deps */
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import {
  pushCountry,
  pushSingleCountryDashboard,
} from "../../../../../middlewares/sysNavigation/SysNavigation";
import { useCountryContext } from "../../../../../context/country/countryContext/CountryContext";
import { BreadCrumbComponent } from "../../../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  getOneRegion,
  addOneCountry,
  cleanOneLevel,
} from "../../../../../middlewares/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../../../../context/admin/context/AdminContext";
import { Pagination } from "../../../component/Component";

import { FiSearch, FiX, FiEye, FiEyeOff, FiTrash2, FiEdit2, FiGrid } from "react-icons/fi";
import { HiOutlineCollection } from "react-icons/hi";
import { AiOutlineLoading } from "react-icons/ai";

/* Routes (unchanged) */
const addRoute = "/countries/add";
const editCountryRoute = "/countries/edit";
const singleCountryDashord = "/country/dashboard";
const addContent = "/dosee/data";
const levels = "/levels/first";

const Overview = () => {
  const [countriesList, setCountriesList] = useState([]);
  const { countries, fetchCountries, count } = useCountryContext();
  const { sysAdmin } = useAdminContext();

  // pagination state (unchanged)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // ui-only
  const [searchKey, setSearchKey] = useState("");

  useEffect(() => {
    if (!countries) fetchCountries(page, pageSize);
    else {
      setCountriesList(
        countries
          ?.slice()
          ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
      );
    }
  }, [countries]); // keep original behavior

  // region filter id (breadcrumb selection)
  const regionFilterId = useMemo(
    () => (getOneRegion().length > 0 ? getOneRegion()[0]?.id : null),
    [getOneRegion]
  );

  // search/filter
  const SearchList = (value) => {
    const key = String(value || "").toLowerCase();
    setSearchKey(value);
    if (!key) {
      setCountriesList(
        countries
          ?.slice()
          ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
      );
      return;
    }
    const searchArray = [];
    countries?.forEach((country) => {
      if (String(country?.name).toLowerCase().includes(key)) searchArray.push(country);
    });
    setCountriesList(
      searchArray?.slice()?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
    );
  };

  const superAdminMode = () => ["super_admin", "admin"].includes(sysAdmin?.role);

  // filtered by region (presentation only)
  const visibleCountries = useMemo(() => {
    const base = countriesList ?? [];
    if (!regionFilterId) return base;
    return base.filter((c) => c?.continent_region_id === regionFilterId);
  }, [countriesList, regionFilterId]);

  const totalVisible = visibleCountries?.length || 0;

  return (
    <div className="p-3 lg:p-4 h-full overflow-y-auto rounded-b-xl bg-gray-50">
      <div className="flex flex-col gap-4">
        <BreadCrumbComponent default_value={true} />

        {/* Toolbar */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 p-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Countries</h2>
                <p className="text-[11px] text-gray-600">
                  Search, manage, and jump into dashboards or content. Region filter is applied automatically if selected.
                </p>
              </div>
              <span className="hidden md:inline-flex h-6 items-center rounded-full bg-gray-100 px-2 text-[10px] font-semibold text-gray-700">
                {totalVisible} {totalVisible === 1 ? "country" : "countries"}
              </span>
            </div>

            {/* Search + Clear + Add */}
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-72">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchKey}
                  onChange={(e) => SearchList(e.target.value)}
                  placeholder="Search countries…"
                  className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-8 text-xs focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                />
                {searchKey ? (
                  <button
                    type="button"
                    aria-label="Clear"
                    onClick={() => SearchList("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                ) : null}
              </div>

              {superAdminMode() ? (
                <Link to={addRoute} className="sm:ml-1">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-gray-800"
                  >
                    Add Country
                  </button>
                </Link>
              ) : null}
            </div>
          </div>

          <div className="h-[2px] w-full bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-500 opacity-20" />
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed text-xs">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left font-bold text-gray-700 w-1/3">Name</th>
                  <th className="px-3 py-3 text-left font-bold text-gray-700 w-1/6">Status</th>
                  <th className="px-3 py-3 text-center font-bold text-gray-700 w-1/3">Actions</th>
                  {superAdminMode() ? (
                    <th className="px-3 py-3 text-center font-bold text-gray-700 w-1/6">Levels &amp; Cities</th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleCountries && visibleCountries.length > 0 ? (
                  visibleCountries.map((country) => (
                    <Row
                      key={country?.country_id}
                      country={country}
                      superAdmin={superAdminMode()}
                    />
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-3 py-8 text-center text-gray-500"
                      colSpan={superAdminMode() ? 4 : 3}
                    >
                      No countries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination (logic unchanged) */}
        <Pagination
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total_items={count}
          callbackFunction={(_page_, _pageSize_) => {
            fetchCountries(_page_, _pageSize_);
          }}
        />
      </div>
    </div>
  );
};

const Row = ({ country, superAdmin }) => {
  const { name, status } = country || {};
  return (
    <tr className="hover:bg-gray-50/80 transition-colors">
      <td className="px-3 py-3 text-gray-900">{name}</td>

      {/* Status chip */}
      <td className="px-3 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold ${status === 1
              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
              : "bg-red-50 text-red-700 ring-1 ring-red-100"
            }`}
        >
          {status === 1 ? "Visible" : "Hidden"}
        </span>
      </td>

      {/* Actions: single Manage button → action sheet with confirmations */}
      <td className="px-3 py-2 text-center">
        <ActionsSheet country={country} superAdmin={superAdmin} />
      </td>

      {/* Levels */}
      {superAdmin ? (
        <td className="px-3 py-2 text-center">
          <LevelsAction props={country} />
        </td>
      ) : null}
    </tr>
  );
};

const LevelsAction = ({ props }) => {
  return (
    <Link to={levels}>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-3 py-2 text-[11px] font-semibold text-white hover:bg-gray-800"
        onClick={() => {
          // For BreadCrumb only
          addOneCountry({
            id: props.country_id,
            name: props.name,
            slug: props.slug,
            route: "/countries",
            data: props,
          });
          // For filtering and display
          pushCountry({
            country: {
              id: props.country_id,
              name: props.name,
              slug: props.slug,
              route: "/countries",
            },
          });
        }}
      >
        Open
      </button>
    </Link>
  );
};

/** Actions in a clean “Manage” sheet + per-action confirm modals */
const ActionsSheet = ({ country, superAdmin }) => {
  const navigate = useNavigate();
  const {
    countries,
    deleteCountry,
    updateCountryState,
    updateCountry,
    fetchCountries,
  } = useCountryContext();

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: 'toggle'|'dash'|'content'|'edit'|'delete' }

  const country_id = country?.country_id;
  const country_name = country?.name;
  const status = country?.status;

  // actions (same logic as before)
  const goDashboard = () => {
    pushSingleCountryDashboard(country_name);
    navigate(`${singleCountryDashord}/${country_id}`);
  };

  const goContent = () => {
    // BreadCrumb + target context
    addOneCountry({
      id: country.country_id,
      name: country.name,
      slug: country.slug,
      route: "/countries",
      data: country,
    });
    cleanOneLevel();
    pushCountry({
      country: {
        id: country.country_id,
        name: country.name,
        slug: country.slug,
        route: "/countries",
      },
    });
    navigate(addContent);
  };

  const goEdit = () => navigate(`${editCountryRoute}/${country_id}`);

  const doDelete = async () => {
    const { status: code } = await deleteCountry(country_id);
    if (code === 200) {
      if (countries) {
        updateCountryState(
          countries.filter((_c) => _c.country_id !== country_id)
        );
      }
      setConfirm(null);
      setOpen(false);
    }
  };

  const doToggle = async () => {
    const payload = { ...country, status: country.status === 0 ? 1 : 0 };
    delete payload.country_id;
    const { status: code } = await updateCountry(country_id, payload);
    if (code === 200) {
      await fetchCountries(1, 100);
      setConfirm(null);
      setOpen(false);
    }
  };

  const items = [
    {
      key: "toggle",
      label: status === 1 ? "Hide Country" : "Make Visible",
      icon: status === 1 ? <FiEyeOff /> : <FiEye />,
      tone:
        status === 1
          ? "text-red-700 bg-red-50 ring-red-100"
          : "text-emerald-700 bg-emerald-50 ring-emerald-100",
      hidden: !superAdmin ? false : false,
      action: () => setConfirm({ type: "toggle" }),
      confirm: {
        title: status === 1 ? "Hide this country?" : "Make this country visible?",
        body:
          status === 1
            ? `This will hide ${country_name} from public views.`
            : `This will set ${country_name} to visible.`,
        confirmText: status === 1 ? "Hide" : "Make Visible",
        onConfirm: doToggle,
      },
    },
    {
      key: "dash",
      label: "Open Dashboard",
      icon: <FiGrid />,
      tone: "text-gray-800 bg-gray-50 ring-gray-100",
      action: () => setConfirm({ type: "dash" }),
      confirm: {
        title: "Open Country Dashboard?",
        body: `Proceed to ${country_name} dashboard.`,
        confirmText: "Open",
        onConfirm: goDashboard,
      },
    },
    {
      key: "content",
      label: "Open Content",
      icon: <HiOutlineCollection />,
      tone: "text-gray-800 bg-gray-50 ring-gray-100",
      action: () => setConfirm({ type: "content" }),
      confirm: {
        title: "Open Content Workspace?",
        body: `Proceed to content workspace for ${country_name}.`,
        confirmText: "Open",
        onConfirm: goContent,
      },
    },
    {
      key: "edit",
      label: "Edit Country",
      icon: <FiEdit2 />,
      tone: "text-blue-700 bg-blue-50 ring-blue-100",
      hidden: !superAdmin,
      action: () => setConfirm({ type: "edit" }),
      confirm: {
        title: "Edit Country?",
        body: `Open edit form for ${country_name}.`,
        confirmText: "Edit",
        onConfirm: goEdit,
      },
    },
    {
      key: "delete",
      label: "Delete Country",
      icon: <FiTrash2 />,
      tone: "text-red-700 bg-red-50 ring-red-100",
      hidden: !superAdmin,
      action: () => setConfirm({ type: "delete" }),
      confirm: {
        title: "Delete Country?",
        body: `This action cannot be undone. Delete ${country_name}?`,
        confirmText: "Delete",
        danger: true,
        onConfirm: doDelete,
      },
    },
  ].filter((i) => !i.hidden);

  const currentConfirm = confirm
    ? items.find((i) => i.key === confirm.type)?.confirm
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-[11px] font-semibold text-gray-800 hover:bg-gray-50"
      >
        Manage
      </button>

      {/* Action Sheet */}
      {open ? (
        <Sheet title={`Manage ${country_name}`} onClose={() => setOpen(false)}>
          <div className="grid grid-cols-1 gap-2">
            {items.map((item) => (
              <button
                key={item.key}
                onClick={item.action}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-left ring-1 ${item.tone} hover:opacity-90`}
              >
                <span className="flex items-center gap-2 text-xs font-semibold">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/70">
                    {item.icon}
                  </span>
                  {item.label}
                </span>
                <span className="text-[10px] font-medium text-gray-500">Confirm</span>
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}

      {/* Per-action confirm */}
      {currentConfirm ? (
        <Confirm
          title={currentConfirm.title}
          body={currentConfirm.body}
          confirmText={currentConfirm.confirmText}
          danger={currentConfirm.danger}
          onCancel={() => setConfirm(null)}
          onConfirm={currentConfirm.onConfirm}
        />
      ) : null}
    </>
  );
};

/** Minimal sheet with soft gradient edge, no heavy blur */
const Sheet = ({ title, children, onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end sm:items-center sm:justify-center p-0 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full sm:max-w-md overflow-hidden rounded-t-2xl sm:rounded-2xl border border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-emerald-200/30 via-amber-200/30 to-purple-200/30 blur-xl"
        />
        <div className="relative p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

/** Reusable confirmation dialog with gentle gradient accent */
const Confirm = ({ title, body, confirmText, danger, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-emerald-200/30 via-amber-200/30 to-purple-200/30 blur-xl"
        />
        <div className="relative p-5">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-xs text-gray-700">{body}</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handle}
              disabled={busy}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold text-white ${danger
                  ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
                  : "bg-gray-900 hover:bg-gray-800 focus-visible:ring-emerald-500"
                } disabled:cursor-not-allowed`}
            >
              {busy ? <AiOutlineLoading className="h-4 w-4 animate-spin" /> : null}
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
